import type {
  AuthResponse,
  AuthUser,
  LoginCredentials,
  LoginDispatchCredentials,
  LoginDispatchResponse,
  RequestTokenResponse,
  SignupCredentials,
  SignupResponse,
  VerifyTokenCredentials,
} from '@/types/auth/authTypes';

import { apiGet, apiPost, apiPut } from '../../../shared/utils/requestBuilder';

export const authService = {
  dispatchLogin: async (credentials: LoginDispatchCredentials): Promise<LoginDispatchResponse> => {
    const response = await apiPost('/auth/login', credentials, true);

    if (!response.ok) {
      const errorData = await response
        .json()
        .catch(() => ({ error: { message: 'Erro ao iniciar login' } }));
      throw new Error(errorData.error?.message || errorData.error || 'Erro ao iniciar login');
    }

    const raw = await response.json();
    const payload =
      (raw as { data?: LoginDispatchResponse }).data ?? (raw as LoginDispatchResponse);
    return payload;
  },

  requestToken: async (credentials: LoginCredentials): Promise<RequestTokenResponse> => {
    const response = await apiPost('/auth/request-token', credentials, true);

    if (!response.ok) {
      const errorData = await response
        .json()
        .catch(() => ({ error: { message: 'Erro ao solicitar c�digo' } }));
      throw new Error(
        errorData.error?.message || errorData.error || 'Erro ao solicitar c�digo de autentica��o',
      );
    }

    const raw = await response.json();
    const payload = (raw as { data?: RequestTokenResponse }).data ?? (raw as RequestTokenResponse);
    return payload;
  },

  verifyToken: async (credentials: VerifyTokenCredentials): Promise<AuthResponse> => {
    const response = await apiPost('/auth/verify-token', credentials, true);

    if (!response.ok) {
      const errorData = await response
        .json()
        .catch(() => ({ error: { message: 'C�digo inv�lido' } }));
      throw new Error(
        errorData.error?.message || errorData.error || 'C�digo de autentica��o inv�lido',
      );
    }

    const raw = await response.json();
    const payload = (raw as { data?: AuthResponse }).data ?? (raw as AuthResponse);
    return payload;
  },

  verifyLoginToken: async (token: string): Promise<AuthResponse> => {
    const response = await apiGet(`/auth/verify-login-token/${token}`, undefined, true);

    if (!response.ok) {
      const errorData = await response
        .json()
        .catch(() => ({ error: { message: 'Link inv�lido' } }));
      throw new Error(
        errorData.error?.message || errorData.error || 'Link de acesso inv�lido ou expirado',
      );
    }

    const raw = await response.json();
    const payload = (raw as { data?: AuthResponse }).data ?? (raw as AuthResponse);
    return payload;
  },

  logout: async (sessaoId?: string): Promise<void> => {
    try {
      await apiPost('/auth/logout', sessaoId ? { sessaoId } : {});
    } catch (error) {
      console.error('Erro ao fazer logout:', error);
    }
  },

  getCurrentUser: async (): Promise<AuthUser> => {
    const response = await apiGet('/auth/me');

    if (!response.ok) {
      throw new Error('Erro ao buscar dados do usu�rio');
    }

    const raw = await response.json();
    const payload = (raw as { data?: AuthUser }).data ?? (raw as AuthUser);
    return payload;
  },

  signup: async (credentials: SignupCredentials): Promise<SignupResponse> => {
    const response = await apiPost('/auth/signup', credentials, true);

    if (!response.ok) {
      const errorData = await response
        .json()
        .catch(() => ({ error: { message: 'Erro ao criar conta' } }));
      throw new Error(errorData.error?.message || errorData.error || 'Erro ao criar conta');
    }

    const raw = await response.json();
    const payload = (raw as { data?: SignupResponse }).data ?? (raw as SignupResponse);
    return payload;
  },

  verifyEmail: async (token: string): Promise<AuthResponse> => {
    const response = await apiPost('/auth/verify-email', { token }, true);

    if (!response.ok) {
      const errorData = await response
        .json()
        .catch(() => ({ error: { message: 'Token inv�lido' } }));
      throw new Error(
        errorData.error?.message || errorData.error || 'Token de verifica��o inv�lido ou expirado',
      );
    }

    const raw = await response.json();
    const payload = (raw as { data?: AuthResponse }).data ?? (raw as AuthResponse);
    return payload;
  },

  getSessions: async () => {
    const response = await apiGet('/auth/sessions');

    if (!response.ok) {
      throw new Error('Erro ao buscar sess�es');
    }

    const raw = await response.json();
    const payload = (raw as { data?: unknown }).data ?? raw;
    return payload as unknown;
  },

  revokeSession: async (sessionId: string): Promise<void> => {
    const response = await apiPost(`/auth/sessions/${sessionId}/revoke`);

    if (!response.ok) {
      throw new Error('Erro ao revogar sess�o');
    }
  },

  updateProfile: async (
    userId: string,
    data: { nomeCompleto?: string; perfilCompleto?: boolean },
  ): Promise<void> => {
    const response = await apiPut(`/usuarios/${userId}`, data);

    if (!response.ok) {
      const errorData = await response
        .json()
        .catch(() => ({ error: { message: 'Erro ao atualizar perfil' } }));
      throw new Error(errorData.error?.message || errorData.error || 'Erro ao atualizar perfil');
    }
  },
};
